import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_ICON, useSidebar } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const APP_BODY_ID = 'app-body';
/** Matches useChatWidget: chat bottom when footer visible / not visible */
const CHAT_BOTTOM_FOOTER_VISIBLE = 76;
const CHAT_BOTTOM_DEFAULT = 10;
/** Scale + fade-in animation similar to requested snippet */
const TRANSITION = {
  duration: 0.8,
  delay: 0.5,
  ease: [0, 0.71, 0.2, 1.01] as const,
};
const SCROLL_STOP_DELAY = 200;

const parsePx = (value: string) => Number.parseInt(value.replace('px', ''), 10);

const getSidebarLeft = (isMobile: boolean, isCollapsed: boolean): number => {
  if (isMobile || typeof window === 'undefined') return 16;
  if (isCollapsed) return parsePx(SIDEBAR_WIDTH_ICON) + 16;
  return parsePx(SIDEBAR_WIDTH) + 16;
};

const getScrollElement = () => document.getElementById(APP_BODY_ID);

const PageScrollToTop = () => {
  const { t } = useTranslation('ui-business');
  const { isFooterVisible } = useChatWidget();
  const { isCollapsed, isMobile } = useSidebar();
  const [visible, setVisible] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const bottom = isFooterVisible ? CHAT_BOTTOM_FOOTER_VISIBLE : CHAT_BOTTOM_DEFAULT;
  const left = useMemo(() => getSidebarLeft(isMobile, isCollapsed), [isMobile, isCollapsed]);

  const handleScroll = useCallback(() => {
    const el = getScrollElement();
    const scrollTop = el ? el.scrollTop : window.scrollY;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    if (scrollTop === 0) {
      setVisible(false);
      scrollTimeoutRef.current = null;
      return;
    }

    setVisible(false);
    scrollTimeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, SCROLL_STOP_DELAY);
  }, []);

  useEffect(() => {
    const el = getScrollElement();
    const target = el ?? window;
    handleScroll();
    target.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      target.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  const scrollToTop = useCallback(() => {
    const el = getScrollElement();
    (el ?? window).scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1, bottom }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={TRANSITION}
          style={{ position: 'fixed', left, zIndex: 1002 }}
          className='rounded-full shadow-lg ring-1 ring-gray-6'
        >
          <Tooltip content={t('scroll_to_top')} align='end'>
            <Button
              variant='surface'
              color='primary'
              size='regular'
              icon={<Icon name='chevron-up' />}
              onClick={scrollToTop}
              aria-label={t('scroll_to_top')}
              data-testid='page-scroll-to-top'
              className='rounded-full'
            />
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageScrollToTop;
